import React, { useEffect, useState, useRef } from 'react'
import ReferenceModal from './ReferenceModal'

function getAvatarUrl(url) {
  if (!url) return null
  if (url.includes('assets.auxodev.dk') && !url.includes('/cdn-cgi/image/')) {
    return url.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=160,height=160,fit=cover,quality=85,format=auto/')
  }
  return url
}

const MAX_CHAR_LENGTH = 200

function formatPreview(text, maxLen = MAX_CHAR_LENGTH) {
  if (!text) return { text: '', isTruncated: false }
  if (text.length <= maxLen) return { text, isTruncated: false }

  const sub = text.slice(0, maxLen)
  const lastSpace = sub.lastIndexOf(' ')
  const truncated = (lastSpace > 0 ? sub.slice(0, lastSpace) : sub).trim() + '...'
  return { text: truncated, isTruncated: true }
}

export default function InfiniteReferencesSlider() {
  const [references, setReferences] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedModalIndex, setSelectedModalIndex] = useState(null)
  const scrollRef = useRef(null)

  useEffect(() => {
    async function loadReferences() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/referencer')
        if (res.ok) {
          const data = await res.json()
          setReferences(data.items || [])
        }
      } catch (err) {
        console.error('Failed to load references', err)
      } finally {
        setLoading(false)
      }
    }
    loadReferences()
  }, [])

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -380, behavior: 'smooth' })
    }
  }

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 380, behavior: 'smooth' })
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (!references || references.length === 0) return null

  return (
    <div className="relative group py-4">
      {/* Navigation Arrows */}
      <button
        onClick={handleScrollLeft}
        aria-label="Forrige reference"
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-5 w-11 h-11 bg-white border border-slate-300 text-black hover:bg-slate-100 flex items-center justify-center transition-all cursor-pointer z-30 focus:outline-none shadow-sm"
      >
        <i className="fa-solid fa-chevron-left text-sm text-black" aria-hidden="true" />
      </button>

      <button
        onClick={handleScrollRight}
        aria-label="Næste reference"
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-5 w-11 h-11 bg-white border border-slate-300 text-black hover:bg-slate-100 flex items-center justify-center transition-all cursor-pointer z-30 focus:outline-none shadow-sm"
      >
        <i className="fa-solid fa-chevron-right text-sm text-black" aria-hidden="true" />
      </button>

      {/* Subtle fade masks on left and right edges */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-slate-50 to-transparent z-10" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-slate-50 to-transparent z-10" />

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto scroll-smooth no-scrollbar py-2 px-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {references.map((ref, idx) => {
          const avatar = Array.isArray(ref.billede) ? ref.billede[0] : ref.billede
          const { text: previewText, isTruncated } = formatPreview(ref.anbefaling, MAX_CHAR_LENGTH)

          return (
            <div
              key={`${ref.id || idx}-${idx}`}
              className="w-[320px] sm:w-[420px] lg:w-[460px] shrink-0 bg-white p-6 sm:p-7 border border-slate-200 flex flex-col justify-between shadow-xs"
            >
              <div>
                <div className="flex items-center gap-1 text-black mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="fa-solid fa-star text-xs text-black" aria-hidden="true" />
                  ))}
                </div>
                <p className="text-black text-sm italic leading-relaxed whitespace-pre-line">
                  "{previewText}"
                </p>

                {isTruncated && (
                  <button
                    onClick={() => setSelectedModalIndex(idx)}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-black hover:text-slate-600 underline underline-offset-4 cursor-pointer transition-colors"
                  >
                    <span>Læs mere</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </button>
                )}
              </div>

              <div className="flex items-start gap-3.5 pt-4 border-t border-slate-200 mt-6">
                {avatar ? (
                  <img
                    src={getAvatarUrl(avatar)}
                    alt={ref.navn}
                    className="w-12 h-12 rounded-full object-cover border border-slate-300 shrink-0 bg-slate-100"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-100 border border-slate-300 shrink-0 flex items-center justify-center font-bold text-black text-sm">
                    {ref.navn ? ref.navn.charAt(0) : 'R'}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h4 className="font-bold text-black text-sm leading-snug">{ref.navn}</h4>
                  <p className="text-xs text-slate-800 font-medium mt-0.5 line-clamp-2" title={ref.titel}>
                    {ref.titel}
                    {ref.virksomhed ? ` · ${ref.virksomhed}` : ''}
                  </p>
                  {ref.relation && (
                    <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">
                      {ref.relation}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Full Modal Viewer with Prev/Next Navigation */}
      <ReferenceModal
        references={references}
        currentIndex={selectedModalIndex ?? 0}
        isOpen={selectedModalIndex !== null}
        onClose={() => setSelectedModalIndex(null)}
        onSelectIndex={(idx) => setSelectedModalIndex(idx)}
      />
    </div>
  )
}
